<template>
  <ClientOnly>
    <div v-if="hasAvailableItems(items)">
      <Teleport to="#checkout-side-cart-submit">
        <CheckoutButton
          class="my-24"
          data-qa="go-to-next-side"
          :disabled="isNextButtonDisabled"
          full-width="always"
          :loading="isLoading"
          @next="emit('next')"
        />
      </Teleport>
    </div>
  </ClientOnly>

  <div v-if="hasItems">
    <CartItems
      :items
      @next="emit('next', false)"
      @updated="emit('update')"
      @updating="emit('updating', $event)"
    >
      <FlashSalesWarning
        class="mb-12"
        :items
        @updated="emit('update')"
        @updating="emit('updating')"
      />
    </CartItems>

    <slot />
    <ReassuranceItems />

    <StickyBar
      :price
      :price-with-swap="summary.totalPriceAfterBuyback"
      :with-mobile-plan="getSelectedMobilePlan(items)?.type === 'ONLINE'"
      :with-swap="swapStore.hasOffer"
    >
      <template #button>
        <CheckoutButton
          class="max-w-200 grow-0 self-center truncate"
          :disabled="isNextButtonDisabled"
          full-width="never"
          :loading="isLoading"
          @next="emit('next')"
        />
      </template>
    </StickyBar>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import ReassuranceItems from '~/scopes/checkout/components/ReassuranceItems/ReassuranceItems.vue'
import { useSwapStore } from '~/scopes/checkout/stores/swapStore'
import {
  hasAvailableItems,
  hasOutOfStockItem,
} from '~/scopes/checkout/utils/cart'
import { getSelectedMobilePlan } from '~/scopes/checkout/utils/getSelectedMobilePlan'

import CartItems from '../CartItems/CartItems.vue'
import CheckoutButton from '../CheckoutButton/CheckoutButton.vue'
import FlashSalesWarning from '../FlashSalesWarning/FlashSalesWarning.vue'
import StickyBar from '../StickyBar/StickyBar.vue'

const swapStore = useSwapStore()

const props = defineProps<{
  items: CartItem[]
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'updating', updatedItem?: CartItem): void
  (e: 'next', trackClick?: boolean): void
}>()

const hasItems = computed(() => !isEmpty(props.items))
const isNextButtonDisabled = computed(() => {
  return hasOutOfStockItem(props.items) || props.isLoading
})

const price = computed(() => {
  return {
    amount: props.summary.totalPrice,
    currency: props.summary.totalListingsPrice.currency,
  }
})

onMounted(async () => {
  sessionStorage.setItem('showCatchupModal', 'true')
})
</script>
